/**
 * The domain model, with no I/O and no dependency on how any of it is stored.
 *
 * Persisted shapes use ISO strings rather than `Date`, because these objects go
 * into DynamoDB and come back out of JSON. A `Date` that survives a write and
 * returns as a string is the kind of type lie that typechecks all the way to
 * production.
 */
/**
 * The name of a language, for telling a model which one to write in.
 *
 * **A locale code is not an instruction.** Every generation prompt used to say "write
 * in the same language as the objective", which asks the model to infer the language
 * from data it has been told to treat as untrusted — and inference is exactly what
 * drifts. A curriculum for an English objective, built from documents whose own front
 * matter declared `language="en"`, came back entirely in German. Nothing was
 * misconfigured; the instruction was simply relative where it needed to be absolute.
 *
 * So prompts name the language. This is the only place that mapping lives, because
 * three pipelines need the same answer and three copies of it would drift apart.
 */
export const LANGUAGE_NAMES = {
    en: 'English',
    es: 'Spanish (Latin American)',
};
export function languageName(locale) {
    return LANGUAGE_NAMES[locale];
}
/**
 * A smoke test for "did the model write in the language it was told to".
 *
 * Not language detection, and not trying to be: it looks for a handful of words that
 * are near-unavoidable in running prose in the expected language, and complains only
 * when *none* of them appear across a whole curriculum's worth of text. That is a
 * deliberately low bar — it cannot tell good Spanish from bad, and it will not fire on
 * a few borrowed terms, which is right, because technical material is full of them.
 *
 * It exists because the failure it catches is expensive and quiet. A learner
 * memorises a curriculum; one silently generated in the wrong language wastes the
 * research it was built from and every item generated afterwards. Failing the pass
 * costs one cheap re-run.
 */
const LANGUAGE_MARKERS = {
    en: ['the', 'and', 'of', 'for', 'with', 'that', 'from', 'which'],
    es: ['el', 'la', 'los', 'las', 'de', 'que', 'para', 'con', 'del', 'y'],
};
export function looksLikeLanguage(text, locale) {
    // Short text carries no signal — a handful of titles legitimately contains no
    // function words at all, and failing on that would be worse than not checking.
    const words = text.toLowerCase().match(/[\p{L}]+/gu) ?? [];
    if (words.length < 40)
        return true;
    const markers = new Set(LANGUAGE_MARKERS[locale]);
    return words.some((word) => markers.has(word));
}
/**
 * What an objective that never said gets.
 *
 * Research, because that is what every objective did before the choice existed and because it
 * is the answer that cannot lose material: an objective wrongly treated as uploads-only sits
 * waiting for pages nobody is going to photograph, and waits silently.
 */
export const DEFAULT_PLAN_SOURCE = 'research';
export function isPlanSource(value) {
    return value === 'research' || value === 'uploads' || value === 'scoped';
}
/**
 * Which source a stored objective row declares.
 *
 * **One reader, because six call sites would be six chances to get the default backwards.**
 * The pipelines that consult this are spread across three packages — the API refusing a
 * research pass, `research.ts` refusing to spend, `extract.ts` deciding an upload is the thing
 * that builds the first plan — and they all take a raw DynamoDB row rather than a typed
 * `Objective`. A row is also the shape an unrecognised value arrives in, and this is where it
 * is turned back into the default rather than trusted into a `switch`.
 */
export function planSourceOf(row) {
    const declared = row?.['planFrom'];
    return isPlanSource(declared) ? declared : DEFAULT_PLAN_SOURCE;
}
/**
 * Whether this objective ever reaches a research model, by any route.
 *
 * Kept as its own question because several callers only want the coarse answer — may anything
 * at all be spent here — and spelling it `!== 'uploads'` at each of them is how the two halves
 * of F-118's refusal drift apart. Where the answer depends on *which* pass, `allowsResearch` is
 * the one to ask; this one is now too coarse to be a gate on spending, and F-119 moved every
 * gate that was using it as one.
 */
export function researches(source) {
    switch (source) {
        case 'research':
            return true;
        case 'uploads':
            return false;
        case 'scoped':
            return true;
    }
}
/**
 * Whether this objective may run this particular research pass.
 *
 * **An exhaustive `switch` on purpose, and that is the mechanism F-118 asked for rather than a
 * comment.** Its entry says a third `PlanSource` member must not be declarable without every
 * branch being revisited; a `switch` with no `default`, over a union whose every member returns,
 * is how TypeScript enforces it — add a member and this stops compiling, in the one function
 * every gate consults.
 *
 * Each cell of the table is a spend that must not happen:
 *
 * | | `full` | `gaps` | `subject` | `scoped` |
 * | --- | --- | --- | --- | --- |
 * | `research` | yes | yes | yes | no |
 * | `uploads` | no | no | no | no |
 * | `scoped` | **no** | **no** | yes | yes |
 *
 * `scoped` refusing `full` is the whole reason the value exists: `full` outlines from the
 * objective text and four web searches, which is exactly the syllabus-beside-the-material the
 * learner opted out of. It refuses `gaps` for the same reason one step removed — a gaps pass
 * asks the open web what the *topic* contains that the outline missed, so every subject it can
 * propose is by construction one the learner's own material does not cover.
 *
 * It allows `subject`, and that is not an oversight. "Go deeper on this one" is pointed at a
 * subject the learner's own chapter named, so researching it further is *filling in around my
 * material* rather than beside it. It is also the cheapest control in the system
 * (`SUBJECT_BUDGET_USD`), and withholding it would leave a scoped objective with no way at all
 * to ask for more about a chapter it already has.
 *
 * `research` refusing `scoped` is the mirror: the scoped outline reads the content store, and a
 * researched objective with an empty store would get an outline written out of nothing.
 */
export function allowsResearch(source, mode) {
    switch (source) {
        case 'research':
            return mode !== 'scoped';
        case 'uploads':
            return false;
        case 'scoped':
            return mode === 'scoped' || mode === 'subject';
    }
}
/**
 * Whether this objective's *first* plan is built out of the pages the learner adds.
 *
 * True for `uploads` for F-118's reason — there is nothing else — and true for `scoped` for a
 * reason worth saying out loud, because it looks at first like the opposite: a scoped objective
 * researches, so one might expect its first plan to wait for the research the way a `research`
 * objective's does. It must not, and two separate failures follow from making it.
 *
 * The first is a stall. A scoped objective's research is started *by* the upload settling, so a
 * pass that fails before it writes anything leaves nothing behind that can ever build a plan —
 * and the learner photographs page after page into silence, which is this project's signature
 * failure arriving from a fourth direction.
 *
 * The second is the product. *"Start from mine and fill in the gaps"* means the learner's own
 * chapter is a plan the moment it has been read, and the research layers into it afterwards.
 * Waiting would make this mode slower than the one it improves on.
 *
 * What stops a plan being built out of a photograph of a cover is the readiness gauge, which
 * measures the material. Not this.
 */
export function buildsFirstPlanFromUploads(source) {
    switch (source) {
        case 'research':
            return false;
        case 'uploads':
            return true;
        case 'scoped':
            return true;
    }
}
/**
 * Whether an upload settling should also set a research pass looking around it. F-119.
 *
 * Only `scoped`. A `research` objective has already had, or will be given, its own outline from
 * the objective text, and an upload on it is material to fold rather than a reason to research
 * again — F-51's two buttons are how its owner asks for that, deliberately. An `uploads`
 * objective reaches no research model at any price.
 */
export function researchesAroundUploads(source) {
    switch (source) {
        case 'research':
            return false;
        case 'uploads':
            return false;
        case 'scoped':
            return true;
    }
}
/**
 * Whether a node is in play, following its parent when it has one.
 *
 * Selection lives only on top-level nodes, so a nested topic's own `active` is
 * meaningless and is deliberately not consulted: the question "is this being
 * studied" has exactly one answer per subject, and storing it per leaf would let a
 * subtree disagree with the section heading the learner unticked.
 */
export function isActiveNode(node, byId) {
    // Bounded rather than recursive: a `parentId` cycle is a corrupt curriculum, and
    // the honest response is to treat the node as active and let it be seen, not to
    // hang the pass that reads it.
    let current = node;
    for (let depth = 0; depth < 32; depth++) {
        if (!current.parentId)
            return current.active !== false;
        const parent = byId.get(current.parentId);
        // An orphan is treated as its own root, matching how the outline renders it —
        // it is visible to the learner, so it must be answerable here too.
        if (!parent)
            return current.active !== false;
        current = parent;
    }
    return true;
}
//# sourceMappingURL=domain.js.map